import {
  collectionGroup,
  doc,
  type DocumentReference,
  getDoc,
  getDocs,
  getFirestore,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  startAfter,
  Timestamp,
  type Transaction,
  where,
  type CollectionReference,
  type QueryConstraint,
  type QueryDocumentSnapshot,
  type Unsubscribe,
  type UpdateData,
  type WithFieldValue,
} from 'firebase/firestore'
import { getFirebaseApp } from '../config/firebase.config'
import { refs, type ContactDoc } from '../models/base/firestore-refs'
import type { ContactEntity, ContactMessageDoc } from '../models/contact/contact.modl'
import { toContactEntity } from '../models/contact/contact.modl'
import type { PaginatedResult } from './school-account-service'
import type { ContactHistoryDoc } from '../models/contact/contact-history.modl'
import { getContactStatusLabel } from '../utils/contact-status'
import {
  createBaseFields,
  createDoc,
  getOne,
  transactionSetWithBaseFields,
  transactionUpdateWithUpdatedAt,
} from './firestore-crud'

export type CreateContactAsSchoolData = {
  subject: string
  message: string
  projectNumber?: string
  projectId?: string
}

/**
 * Contact（school_accounts/{schoolAccountId}/contacts）用サービス
 */
export class ContactService {
  /**
   * 単一のお問い合わせを購読する（admin 詳細画面などで利用）
   */
  subscribeContactEntity(
    schoolAccountId: string,
    contactId: string,
    onNext: (contact: ContactEntity | null) => void,
    onError?: (error: Error) => void,
    includeDeleted: boolean = false,
  ): Unsubscribe {
    const contactRef = refs.contact(schoolAccountId, contactId)

    return onSnapshot(
      contactRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          onNext(null)
          return
        }

        const data = snapshot.data() as ContactDoc
        if (!includeDeleted && data.is_deleted) {
          onNext(null)
          return
        }

        const contact = toContactEntity(snapshot.id, data, snapshot.ref.path)
        contact.school_account_id = schoolAccountId
        onNext(contact)
      },
      (err) => {
        const e = err instanceof Error ? err : new Error('お問い合わせの購読に失敗しました')
        if (onError) onError(e)
      },
    )
  }

  /**
   * トランザクション内でお問い合わせを ContactDoc として取得する。
   * 存在しない場合は throw する。
   */
  private async getContactDocInTransaction(
    transaction: Transaction,
    contactDocRef: DocumentReference<ContactDoc, ContactDoc>,
  ): Promise<ContactDoc> {
    const snapshot = await transaction.get(contactDocRef)
    if (!snapshot.exists()) {
      throw new Error('お問い合わせが見つかりません')
    }
    return snapshot.data() as ContactDoc
  }
  /**
   * お問い合わせを ContactDoc として取得する。
   * @param schoolAccountId - 学校アカウントID
   * @param contactId - お問い合わせID
   * @returns ContactDoc、存在しなければ null
   */
  async getContactDoc(
    schoolAccountId: string,
    contactId: string,
    includeDeleted: boolean = false,
  ): Promise<{ data: ContactDoc; docRef: DocumentReference<ContactDoc, ContactDoc> } | null> {
    const contactDocRef = refs.contact(schoolAccountId, contactId)
    return getOne(
      contactDocRef,
      (_, data) => ({ data: data as ContactDoc, docRef: contactDocRef }),
      { includeDeleted },
    )
  }
  async getContactEntity(
    schoolAccountId: string,
    contactId: string,
    includeDeleted: boolean = false,
  ): Promise<ContactEntity | null> {
    const contactDocResult = await this.getContactDoc(schoolAccountId, contactId, includeDeleted)
    if (!contactDocResult) {
      return null
    }
    const contactDoc = contactDocResult.data
    const contactDocRef = contactDocResult.docRef
    return toContactEntity(contactDocRef.id, contactDoc, contactDocRef.path)
  }

  /**
   * 学校としてお問い合わせを作成する。
   *
   * @param schoolAccountId - 学校アカウントID
   * @param schoolAccountPath - 学校アカウントのドキュメントパス（school_account 参照用）
   * @param schoolAccountName - 学校名（初回メッセージの sender_name 用）
   * @param data - お問い合わせ内容
   * @returns 作成された Contact の ID、失敗時は null
   */
  async createContactAsSchool(
    schoolAccountId: string,
    schoolAccountPath: string,
    schoolAccountName: string,
    data: CreateContactAsSchoolData,
  ): Promise<string | null> {
    const timestamp = Timestamp.now()
    const initialMessage: ContactMessageDoc = {
      sender: 'school',
      sender_name: schoolAccountName,
      content: data.message.trim(),
      created_at: timestamp,
    }

    const projectPath = data.projectId
      ? `school_accounts/${schoolAccountId}/projects/${data.projectId}`
      : null

    return createDoc(refs.contacts(schoolAccountId), {
      subject: data.subject.trim(),
      message: data.message.trim(),
      status: 'waiting_admin',
      project_id: data.projectId ?? null,
      project_path: projectPath,
      project_number: data.projectNumber?.trim() || null,
      school_account: schoolAccountPath,
      closed_by_admin_account: null,
      messages: [initialMessage],
      response_deadline: null,
    } as WithFieldValue<ContactDoc>, { isDeleted: false })
  }

  /**
   * 指定プロジェクトに紐づくお問い合わせを購読する（admin 案件詳細の問い合わせタブ用）。
   * project_id でフィルタ。インデックス未設定時は全件取得してから projectId でフィルタするフォールバックを行う。
   */
  subscribeContactsByProject(
    schoolAccountId: string,
    projectId: string,
    onNext: (contacts: ContactEntity[]) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    const contactsRef = refs.contacts(schoolAccountId)
    const q = query(
      contactsRef,
      where('is_deleted', '==', false),
      where('project_id', '==', projectId),
      orderBy('created_at', 'desc'),
    )

    const pushContacts = (snapshot: { docs: Array<{ id: string; data: () => ContactDoc; ref: { path: string } }> }) => {
      const list = snapshot.docs
        .map((d) => {
          const c = toContactEntity(d.id, d.data() as ContactDoc, d.ref.path)
          c.school_account_id = schoolAccountId
          return c
        })
        .filter((c) => c.project_id === projectId || c.project_path?.endsWith(`/projects/${projectId}`))
      onNext(list)
    }

    let fallbackUnsub: Unsubscribe | null = null
    const mainUnsub = onSnapshot(
      q,
      (snapshot) => {
        pushContacts(snapshot)
      },
      (err) => {
        if (err instanceof Error && err.message.includes('index')) {
          const fallbackQ = query(
            contactsRef,
            where('is_deleted', '==', false),
            orderBy('created_at', 'desc'),
          )
          fallbackUnsub = onSnapshot(
            fallbackQ,
            (fallbackSnapshot) => {
              pushContacts(fallbackSnapshot)
            },
            (fallbackErr) => {
              const e = fallbackErr instanceof Error ? fallbackErr : new Error('お問い合わせの取得に失敗しました')
              if (onError) onError(e)
            },
          )
        } else {
          const e = err instanceof Error ? err : new Error('お問い合わせの取得に失敗しました')
          if (onError) onError(e)
        }
      },
    )
    return () => {
      mainUnsub()
      if (fallbackUnsub) fallbackUnsub()
    }
  }

  /**
   * school_account 配下の「未削除 & 未クローズ」の contacts を購読する
   * （admin 側の一覧などで利用）
   */
  subscribeActiveForSchoolAccount(
    schoolAccountId: string,
    onNext: (contacts: ContactEntity[]) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    const q = query(
      refs.contacts(schoolAccountId),
      where('is_deleted', '==', false),
      where('status', 'in', ['in_progress', 'irogami_answered', 'waiting_admin']),
    )

    return onSnapshot(
      q,
      (snapshot) => {
        const contacts = snapshot.docs
          .map((d) => {
            const c = toContactEntity(d.id, d.data() as ContactDoc, d.ref.path)
            c.school_account_id = schoolAccountId
            return c
          })
          .sort((a, b) => b.created_at.getTime() - a.created_at.getTime())
        onNext(contacts)
      },
      (err) => {
        const e = err instanceof Error ? err : new Error('Failed to subscribe contacts')
        if (onError) onError(e)
      },
    )
  }

  /**
   * 管理者向け: 全学校横断で「未削除 & 未クローズ」の contacts を collectionGroup で購読する
   */
  subscribeActiveForAdmin(
    onNext: (contacts: ContactEntity[]) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    const db = getFirestore(getFirebaseApp())
    const q = query(
      collectionGroup(db, 'contacts'),
      where('is_deleted', '==', false),
      where('status', 'in', ['in_progress', 'irogami_answered', 'waiting_admin']),
    )

    return onSnapshot(
      q,
      (snapshot) => {
        const contacts = snapshot.docs
          .map((d) => {
            const c = toContactEntity(d.id, d.data() as ContactDoc, d.ref.path)
            // school_accounts/{schoolId}/contacts/{contactId} から schoolId を抽出
            const pathParts = d.ref.path.split('/')
            if (pathParts[0] === 'school_accounts' && pathParts.length >= 2) {
              c.school_account_id = pathParts[1]
            }
            return c
          })
          .sort((a, b) => b.created_at.getTime() - a.created_at.getTime())

        onNext(contacts)
      },
      (err) => {
        const e = err instanceof Error ? err : new Error('Failed to subscribe contacts')
        if (onError) onError(e)
      },
    )
  }

  /**
   * contacts をページング取得する
   *
   * - 並び順: created_at desc（新しい順）
   * - includeDeleted=false のとき is_deleted==false のみ
   */
  async getPaginatedForSchoolAccount(
    schoolAccountId: string,
    pageSize: number,
    lastDocument?: QueryDocumentSnapshot | null,
    includeDeleted: boolean = false,
  ): Promise<PaginatedResult<ContactEntity> | null> {
    const baseConstraints: QueryConstraint[] = []
    if (!includeDeleted) baseConstraints.push(where('is_deleted', '==', false))

    baseConstraints.push(orderBy('created_at', 'desc'))
    baseConstraints.push(limit(pageSize + 1))
    if (lastDocument) baseConstraints.push(startAfter(lastDocument))

    const q = query(refs.contacts(schoolAccountId), ...baseConstraints)
    const snapshot = await getDocs(q)
    const docs = snapshot.docs

    const hasNextPage = docs.length > pageSize
    const dataDocs = hasNextPage ? docs.slice(0, pageSize) : docs
    const lastDoc = dataDocs.length > 0 ? (dataDocs[dataDocs.length - 1] as QueryDocumentSnapshot) : null

    const data = dataDocs.map((d) => {
      const c = toContactEntity(d.id, d.data() as ContactDoc, d.ref.path)
      c.school_account_id = schoolAccountId
      return c
    })

    return { data, lastDoc, hasNextPage }
  }

  /**
   * 対応済み（status === 'completed'）のお問い合わせをページング取得する
   * 学校側のお問い合わせ一覧「対応済み」タブで利用
   */
  async getCompletedContactsPaginated(
    schoolAccountId: string,
    pageSize: number,
    lastDocument?: QueryDocumentSnapshot | null,
  ): Promise<PaginatedResult<ContactEntity>> {
    const baseConstraints: QueryConstraint[] = [
      where('is_deleted', '==', false),
      where('status', '==', 'completed'),
      orderBy('created_at', 'desc'),
      limit(pageSize + 1),
    ]
    if (lastDocument) baseConstraints.push(startAfter(lastDocument))

    const q = query(refs.contacts(schoolAccountId), ...baseConstraints)
    const snapshot = await getDocs(q)
    const docs = snapshot.docs

    const hasNextPage = docs.length > pageSize
    const dataDocs = hasNextPage ? docs.slice(0, pageSize) : docs
    const lastDoc = dataDocs.length > 0 ? (dataDocs[dataDocs.length - 1] as QueryDocumentSnapshot) : null

    const data = dataDocs.map((d) => {
      const c = toContactEntity(d.id, d.data() as ContactDoc, d.ref.path)
      c.school_account_id = schoolAccountId
      return c
    })

    return { data, lastDoc, hasNextPage }
  }

  /**
   * 管理者としてお問い合わせにメッセージを送信する。
   * ContactHistory に更新後スナップショットを保存し、Contact の messages を更新する。
   *
   * @param contact - 更新する Contact（schoolAccountId 必須）
   * @param messageContent - 送信するメッセージ本文
   * @param adminAccount - 送信者（管理者）の id / nameKanji / path
   * @returns 更新後の Contact、失敗時は null
   */
  /**
   * 学校としてお問い合わせにメッセージを送信する。
   */
  async sendMessageAsSchool(
    contact: ContactEntity,
    messageContent: string,
    schoolAccount: { id: string; schoolName: string; path: string },
  ): Promise<ContactEntity | null> {
    const schoolAccountId = contact.school_account_id
    if (!schoolAccountId) {
      throw new Error('school_account_id is missing on contact')
    }

    const newMessageDoc: WithFieldValue<ContactMessageDoc> = {
      sender: 'school' as const,
      sender_name: schoolAccount.schoolName ?? '',
      content: messageContent.trim(),
      created_at: Timestamp.now(),
    }

    const contactDocRef = refs.contact(schoolAccountId, contact.id)
    const historyCollectionRef = refs.contactHistories(schoolAccountId, contact.id)
    const db = getFirestore(getFirebaseApp())

    try {
      await runTransaction(db, async (transaction) => {
        const contactDoc = await this.getContactDocInTransaction(transaction, contactDocRef)

        // 履歴に保存
        const historyPayload: WithFieldValue<ContactHistoryDoc> = {
          contact: contactDoc,
          updated_by_user_type: 'school' as const,
          updated_by_user_id: schoolAccount.id ?? '',
          updated_by_name: schoolAccount.schoolName ?? '',
          updated_by_user_document_path: schoolAccount.path ?? '',
          update_info: 'メッセージ送信',
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
          is_deleted: false,
        }
        const historyDocRef = doc(historyCollectionRef)
        transaction.set(historyDocRef, historyPayload)

        // お問い合わせを更新（Firestore は undefined を許容しないため正規化）
        const newStatus =
          contactDoc.status === 'completed' ? contactDoc.status : ('waiting_admin' as const)
        const normalizedMessages: WithFieldValue<ContactMessageDoc>[] = [
          ...(contactDoc.messages ?? []).map((m) => ({
            sender: (m.sender ?? 'school') as 'school' | 'admin',
            sender_name: m.sender_name ?? '',
            content: m.content ?? '',
            created_at: m.created_at ?? Timestamp.now(),
          })),
          newMessageDoc,
        ]
        const updatedContactDoc: WithFieldValue<Partial<ContactDoc>> = {
          messages: normalizedMessages,
          status: newStatus,
          updated_at: serverTimestamp(),
        }
        transaction.update(contactDocRef, updatedContactDoc)
      })
    } catch (err) {
      console.error('sendMessageAsSchool failed:', err)
      return null
    }

    // 更新後のデータを取得
    const refreshedContactEntity = await this.getContactEntity(schoolAccountId, contact.id)
    if (!refreshedContactEntity) {
      return null
    }
    refreshedContactEntity.school_account_id = schoolAccountId
    return refreshedContactEntity
  }

  /**
   * 管理者としてお問い合わせにメッセージを送信する。
   */
  async sendMessageAsAdmin(
    schoolAccountId: string,
    contactId: string,
    messageContent: string,
    adminAccount: { id: string; nameKanji: string; path: string },
  ): Promise<ContactEntity | null> {
    //今回追加するメッセージ
    const newMessageDoc:WithFieldValue<ContactMessageDoc> = {
      sender: 'admin' as const,
      sender_name: adminAccount.nameKanji ?? '',
      content: messageContent.trim(),
      //　本当はサーバのTimestampを使うべきだが、配列内のデータには利用できないので妥協
      created_at: Timestamp.now(),
    }

    const contactDocRef = refs.contact(schoolAccountId, contactId)
    const historyCollectionRef = refs.contactHistories(schoolAccountId, contactId)
    const db = getFirestore(getFirebaseApp())

    try {
      await runTransaction(db, async (transaction) => {
        const contactDoc = await this.getContactDocInTransaction(transaction, contactDocRef)

        // 履歴に保存
        const historyPayload: WithFieldValue<ContactHistoryDoc> = {
          contact: contactDoc,
          updated_by_user_type: 'admin',
          updated_by_user_id: adminAccount.id ?? '',
          updated_by_name: adminAccount.nameKanji ?? '',
          updated_by_user_document_path: adminAccount.path ?? '',
          update_info: 'メッセージ送信',
          ...createBaseFields(false),
        }
        const historyDocRef = doc(historyCollectionRef)
        transaction.set(historyDocRef, historyPayload)

        // お問い合わせを更新
        const updatedContactDoc: WithFieldValue<Partial<ContactDoc>> = {
          messages: [...(contactDoc.messages ?? []), newMessageDoc],
          status: contactDoc.status,
          updated_at: serverTimestamp(),
        }
        transaction.update(contactDocRef, updatedContactDoc)
      })
    } catch (err) {
      console.error('sendMessageAsAdmin failed:', err)
      return null
    }

    // 更新後のデータを取得
    const refreshedContactEntity = await this.getContactEntity(schoolAccountId, contactId)
    if (!refreshedContactEntity) {
      return null
    }
    return refreshedContactEntity
  }

  /**
   * 管理者としてお問い合わせのステータスを更新する。
   * ContactHistory に更新後スナップショットを保存し、Contact の status（および completed 時は closed_by_admin_account）を更新する。
   *
   * @param contact - 更新する Contact（schoolAccountId 必須）
   * @param newStatus - 新しいステータス
   * @param adminAccount - 更新者（管理者）の id / nameKanji / path
   * @returns 更新後の Contact、失敗時は null
   */
  async updateContactStatusAsAdmin(
    contact: ContactEntity,
    newStatus: 'in_progress' | 'irogami_answered' | 'waiting_admin' | 'completed',
    adminAccount: { id: string; nameKanji: string; path: string },
  ): Promise<ContactEntity | null> {
    const schoolAccountId = contact.school_account_id
    if (!schoolAccountId) {
      throw new Error('school_account_id is missing on contact')
    }

    const contactDocRef = refs.contact(schoolAccountId, contact.id)

    const statusLabel = getContactStatusLabel(newStatus)

    const messagesDoc = (contact.messages ?? []).map((m) => ({
      sender: (m.sender ?? 'admin') as 'school' | 'admin',
      sender_name: m.sender_name ?? '',
      content: m.content ?? '',
      created_at: m.created_at ? Timestamp.fromDate(m.created_at) : serverTimestamp(),
    }))

    // メッセージにステータス更新内容を追加（admin のメッセージとして表示）
    const statusUpdateMessage: ContactMessageDoc = {
      sender: 'admin',
      sender_name: adminAccount.nameKanji ?? '',
      content: `ステータスを「${statusLabel}」に更新しました`,
      created_at: Timestamp.now(),
    }
    const messagesWithStatusUpdate = [...messagesDoc, statusUpdateMessage]

    const closedByAdminAccount =
      newStatus === 'completed'
        ? adminAccount.path
        : contact.status === 'completed'
          ? null
          : contact.closed_by_admin_account ?? null

    const contactSnapshot = {
      subject: contact.subject ?? '',
      message: contact.message ?? '',
      status: newStatus,
      project_id: contact.project_id ?? null,
      project_path: contact.project_path ?? null,
      project_number: contact.project_number ?? null,
      school_account: contact.school_account ?? null,
      closed_by_admin_account: closedByAdminAccount,
      messages: messagesWithStatusUpdate,
      response_deadline: contact.response_deadline
        ? Timestamp.fromDate(contact.response_deadline)
        : null,
      created_at: contact.created_at ? Timestamp.fromDate(contact.created_at) : serverTimestamp(),
      updated_at: serverTimestamp(),
      is_deleted: contact.is_deleted ?? false,
    }

    const historyCollectionRef = refs.contactHistories(schoolAccountId, contact.id)
    const updatePatch: Record<string, string | null | typeof messagesWithStatusUpdate> = {
      status: newStatus,
      messages: messagesWithStatusUpdate,
    }
    if (newStatus === 'completed') {
      updatePatch.closed_by_admin_account = adminAccount.path
    } else if (contact.status === 'completed') {
      updatePatch.closed_by_admin_account = null
    }

    const db = getFirestore(getFirebaseApp())
    await runTransaction(db, async (transaction) => {
      transactionSetWithBaseFields(
        transaction,
        historyCollectionRef as CollectionReference<ContactHistoryDoc, ContactHistoryDoc>,
        {
          contact: contactSnapshot,
          updated_by_user_type: 'admin',
          updated_by_user_id: adminAccount.id ?? '',
          updated_by_name: adminAccount.nameKanji ?? '',
          updated_by_user_document_path: adminAccount.path ?? '',
          update_info: `ステータスを「${statusLabel}」に変更`,
        } as WithFieldValue<ContactHistoryDoc>,
        { isDeleted: false },
      )
      transactionUpdateWithUpdatedAt(
        transaction,
        contactDocRef,
        updatePatch as UpdateData<ContactDoc>,
      )
    })

    const docSnapshot = await getDoc(contactDocRef)
    if (!docSnapshot.exists()) {
      return null
    }

    const refreshed = toContactEntity(
      docSnapshot.id,
      docSnapshot.data() as ContactDoc,
      docSnapshot.ref.path,
    )
    refreshed.school_account_id = contact.school_account_id
    return refreshed
  }
}

